import { useState, useEffect, useCallback } from 'react';
import {
	addItemsToOrder,
	calculateCasePrice,
	CreateCaseArray,
	createDefaultSwappables,
	shuffleArray,
	SortByItemCode,
} from '../utils';

export const useCaseBuilder = (wines, contentfulData) => {
	const [wineSlots, setWineSlots] = useState([]);
	const [swapSlots, setSwapSlots] = useState([]);
	const [pricingSummary, setPricingSummary] = useState({
		listPrice: 0,
		totalPrice: 0,
		totalSavings: 0,
	});
	const [isReady, setIsReady] = useState(false);

	// Case configuration options
	const caseOptions = [
		{
			name: '12x1',
			size: 12,
			unique: 12,
			multiplier: 1,
		},
		{
			name: '6x2',
			size: 12,
			unique: 6,
			multiplier: 2,
		},
		{
			name: '4x3',
			size: 12,
			unique: 4,
			multiplier: 3,
		},
	];

	// Build case when both wines and contentfulData are available
	useEffect(() => {
		if (!wines || wines.length === 0) {
			return;
		}

		const renderCase = () => {
			let finalWineSlots = [];
			let eligibleSwappables = [];
			let swappableWineSlots = [];
			let slotArr = [];
			let swapArr = [];
			let calculatedPricingSummary = {};

			// Filter wines
			const filteredWines = wines
				.filter((obj) => obj.product?.skus?.length) //Remove wines without sku data
				.filter(
					(obj) =>
						obj.product.countryId !== 'ZZ' && obj.product.styleId !== 'NA'
				) //Remove Mystery Wine Single Items
				.filter((obj) => obj.product.vintage !== '1950') //Remove Prepack Single Items
				.filter((obj) => parseInt(obj.product.bottleSize) <= 750) //Remove Magnum Single Items
				.sort(
					(a, b) =>
						b.userItemDetails.sortPriority - a.userItemDetails.sortPriority
				); // sort by recommendation priority

			if (!filteredWines.length) return; // Exit if no wines after filtering

			// Determine case configuration
			const caseSize = contentfulData?.caseSize || '4x3';
			const caseConfig = caseOptions.find((el) => el.name === caseSize) || {};
			const caseUnique = caseConfig.unique || 4;
			const multiplier = caseConfig.multiplier || 3;

			// Slice the top wines based of the Case Unique Value
			finalWineSlots = filteredWines.slice(0, caseUnique);

			//  Slice the Remaining Wines Making sure to Remove the main case wines
			eligibleSwappables = filteredWines.slice(caseUnique);

			// Create swappables
			swappableWineSlots = createDefaultSwappables(
				eligibleSwappables,
				caseUnique
			);

			// Create the Final Main Case By Using Multiplier
			slotArr = CreateCaseArray(finalWineSlots, multiplier);

			// Create the Final Swappable Items By Using Multiplier
			swapArr = CreateCaseArray(swappableWineSlots, multiplier);

			// Clone and process swap array
			swapArr = swapArr.map((item) => structuredClone(item));

			// Set Swap ItemCode to the Final Slots ItemCode
			swapArr = swapArr.map((swapGroup, index) => {
				if (!Array.isArray(swapGroup)) return swapGroup;

				return swapGroup.map((swapItem) => {
					return {
						...swapItem,
						swapItemCode:
							slotArr[index]?.product?.itemCode || swapItem.swapItemCode,
					};
				});
			});

			// Sort Main Case By Item Code
			slotArr = SortByItemCode(slotArr, 'main');

			// Sort Swappables by Item Code
			swapArr = SortByItemCode(swapArr, 'swaps');

			// Calculate case pricing
			calculatedPricingSummary = calculateCasePrice(slotArr, contentfulData);

			// Add items to final order
			addItemsToOrder(slotArr);

			// Update state
			setWineSlots(slotArr);
			setSwapSlots(swapArr);
			setPricingSummary(calculatedPricingSummary);
			setIsReady(true);
		};

		renderCase();
	}, [wines, contentfulData]);

	const handleSwap = useCallback(
		(swapIndex, slotIndex) => {
			if (!wineSlots.length || !swapSlots.length) return;

			const activeMainWine = wineSlots[slotIndex];
			const newMainWine = swapSlots[slotIndex][swapIndex];

			const newWineSlots = [...wineSlots];
			const newSwapSlots = [...swapSlots];

			newWineSlots[slotIndex] = newMainWine;
			newSwapSlots[slotIndex].splice(swapIndex, 1);
			newSwapSlots[slotIndex].push(activeMainWine);
			newSwapSlots[slotIndex] = shuffleArray(newSwapSlots[slotIndex]);

			const newPricingSummary = calculateCasePrice(
				newWineSlots,
				contentfulData
			);

			addItemsToOrder(newWineSlots);

			setWineSlots(newWineSlots);
			setSwapSlots(newSwapSlots);
			setPricingSummary(newPricingSummary);

			return slotIndex;
		},
		[wineSlots, swapSlots, contentfulData]
	);

	return {
		wineSlots,
		swapSlots,
		pricingSummary,
		isReady,
		handleSwap,
	};
};
